import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, ChevronLeft, ChevronRight, Minus, Plus } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Navbar from './Navbar';
import { toast } from 'sonner';
import Product360Viewer from './Product360Viewer';
import ProductReviews from './ProductReviews';
import ProductRecommendations from './ProductRecommendations';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, API, token, toggleFavorite, isFavorite, addToCart } = useApp();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isRotating, setIsRotating] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      if (response.data.sizes?.length > 0) {
        setSelectedSize(response.data.sizes[0]);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleFavoriteClick = async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    const result = await toggleFavorite(product.id);
    if (result) {
      toast.success(isFavorite(product.id) ? t('removed_from_favorites') : t('added_to_favorites'));
    }
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      toast.error(t('please_select_size'));
      return;
    }
    addToCart(product, selectedSize.ml, quantity);
    toast.success(`${product.name} ${t('added_to_cart')}`);
  };

  const getStockStatus = (stock) => {
    if (stock <= 0) return { text: t('out_of_stock'), color: 'text-red-500', available: false };
    if (stock <= 5) return { text: t('low_stock'), color: 'text-orange-500', available: true };
    return { text: t('in_stock'), color: 'text-green-500', available: true };
  };

  // Process images array - handle both full URLs and relative paths
  const processImageUrl = (url) => {
    if (!url) return null;
    // If it's already a full URL, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If it's a relative API path, prepend the backend URL
    if (url.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    }
    return url;
  };

  const images = product?.images?.length > 0 
    ? product.images.map(processImageUrl).filter(Boolean)
    : [product?.image_url].filter(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) return null;

  const stockStatus = getStockStatus(selectedSize?.stock || product.stock);
  const currentPrice = selectedSize?.price || product.price;
  const discount = product.original_price 
    ? Math.round((1 - currentPrice / product.original_price) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Breadcrumb */}
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#A8A29E] hover:text-[#D4AF37] mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft size={20} />
          <span>Back</span>
        </button>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Image Gallery */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative aspect-square rounded-2xl overflow-hidden bg-[#121212]"
              data-testid="product-main-image"
            >
              <img
                src={images[currentImageIndex]}
                alt={product.name}
                className={`w-full h-full object-contain transition-transform duration-500 ${
                  isRotating ? 'animate-spin-slow' : ''
                }`}
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 glass rounded-full hover:bg-white/10"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft size={24} className="text-white" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 glass rounded-full hover:bg-white/10"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight size={24} className="text-white" />
                  </button>
                </>
              )}

              {/* 360 View Button - Now using AI-powered viewer */}
              <Product360Viewer 
                productId={product.id}
                productName={product.name}
                mainImage={images[currentImageIndex]}
                API={API}
              />
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                      currentImageIndex === index ? 'border-[#D4AF37]' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Brand & Name */}
            <div>
              <p className="text-[#D4AF37] font-accent text-sm tracking-wider uppercase mb-2">
                {product.brand}
              </p>
              <h1 className="font-heading text-3xl md:text-4xl text-white mb-2">
                {product.name}
              </h1>
              <p className="text-[#A8A29E] text-sm">
                {product.fragrance_family}
              </p>
            </div>

            {/* Price */}
            <div className="flex items-center gap-4">
              <span className="text-3xl font-bold text-[#D4AF37]">
                €{currentPrice.toFixed(2)}
              </span>
              {product.original_price && (
                <>
                  <span className="text-xl text-[#57534E] line-through">
                    €{product.original_price.toFixed(2)}
                  </span>
                  <span className="bg-[#D4AF37] text-black px-2 py-1 rounded text-sm font-bold">
                    -{discount}%
                  </span>
                </>
              )}
            </div>

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div>
                <h3 className="text-white font-medium mb-3">{t('select_size')}</h3>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => (
                    <button
                      key={size.ml}
                      onClick={() => setSelectedSize(size)}
                      className={`px-6 py-3 rounded-lg border transition-all ${
                        selectedSize?.ml === size.ml
                          ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]'
                          : 'border-[#2C1B18] text-white hover:border-[#D4AF37]/50'
                      }`}
                      data-testid={`size-btn-${size.ml}`}
                    >
                      <span className="font-bold">{size.ml}ml</span>
                      <span className="block text-sm text-[#A8A29E]">
                        €{size.price.toFixed(2)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className={`flex items-center gap-2 ${stockStatus.color}`}>
              <div className={`w-2 h-2 rounded-full ${
                stockStatus.available ? 'bg-green-500' : 'bg-red-500'
              }`} />
              <span className="text-sm">{stockStatus.text}</span>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-4 glass rounded-full px-4 py-2">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="text-white hover:text-[#D4AF37]"
                  data-testid="decrease-qty-btn"
                >
                  <Minus size={20} />
                </button>
                <span className="text-white w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="text-white hover:text-[#D4AF37]"
                  data-testid="increase-qty-btn"
                >
                  <Plus size={20} />
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={!stockStatus.available}
                className="btn-primary flex-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag size={20} />
                {t('add_to_cart')}
              </button>

              <button
                onClick={handleFavoriteClick}
                className={`p-3 rounded-full glass ${
                  isFavorite(product.id) ? 'bg-red-500/20' : ''
                }`}
                data-testid="favorite-btn"
              >
                <Heart 
                  size={24} 
                  className={isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-white'} 
                />
              </button>
            </div>

            {/* Description */}
            <div className="border-t border-[#2C1B18] pt-6">
              <h3 className="text-white font-medium mb-3">{t('description')}</h3>
              <p className="text-[#A8A29E] leading-relaxed">
                {product.description}
              </p>
            </div>

            {/* Fragrance Notes */}
            <div className="grid grid-cols-3 gap-4 border-t border-[#2C1B18] pt-6">
              {/* Top Notes */}
              <div>
                <h4 className="text-[#D4AF37] font-accent text-sm tracking-wider mb-2">
                  {t('top_notes')}
                </h4>
                <ul className="space-y-1">
                  {product.top_notes?.map((note, i) => (
                    <li key={i} className="text-[#A8A29E] text-sm">{note}</li>
                  ))}
                </ul>
              </div>

              {/* Heart Notes */}
              <div>
                <h4 className="text-[#D4AF37] font-accent text-sm tracking-wider mb-2">
                  {t('heart_notes')}
                </h4>
                <ul className="space-y-1">
                  {product.heart_notes?.map((note, i) => (
                    <li key={i} className="text-[#A8A29E] text-sm">{note}</li>
                  ))}
                </ul>
              </div>

              {/* Base Notes */}
              <div>
                <h4 className="text-[#D4AF37] font-accent text-sm tracking-wider mb-2">
                  {t('base_notes')}
                </h4>
                <ul className="space-y-1">
                  {product.base_notes?.map((note, i) => (
                    <li key={i} className="text-[#A8A29E] text-sm">{note}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Product Reviews Section */}
        <ProductReviews productId={product.id} productName={product.name} />
        
        {/* Product Recommendations */}
        <ProductRecommendations productId={product.id} productName={product.name} />
      </div>
    </div>
  );
};

export default ProductDetail;
